import { type FC, useState, useRef, useEffect, Fragment } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { getBuyerId } from 'dibs-cookie-jar';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { HeartPill } from 'dibs-nft-heart-pill/exports/HeartPill';

import { NftItemTileMedia } from './components/NftItemTileMedia';
import { NftItemTileContent } from './components/nftItemTileContent/NftItemTileContent';
import { NftItemTileType } from './helpers/nftItemTileType';
import { ItemInternalContextProvider } from './helpers/ItemInternalContext';
import { MaybeLink } from './helpers/MaybeLink';

import styles from './styles/NftItemTile.scss';

import { type NftItemTile_nftItem$key } from './__generated__/NftItemTile_nftItem.graphql';
import { type NftItemTile_nftAuction$key } from './__generated__/NftItemTile_nftAuction.graphql';
import { type NftItemTile_viewer$key } from './__generated__/NftItemTile_viewer.graphql';
import { type NftItemTile_nftAuctionBidderRef$key } from './__generated__/NftItemTile_nftAuctionBidderRef.graphql';

const { COLLECTOR_TILE_NO_CTA } = NftItemTileType;

type REGULAR_TILE = typeof NftItemTileType.REGULAR_TILE;
type CREATOR_REGULAR = typeof NftItemTileType.CREATOR_REGULAR;
type COLLECTOR_TILE = typeof NftItemTileType.COLLECTOR_TILE;
type COLLECTOR_TILE_NO_CREATOR = typeof NftItemTileType.COLLECTOR_TILE_NO_CREATOR;

interface BaseProps {
    nftItem: NftItemTile_nftItem$key;
    nftAuction?: NftItemTile_nftAuction$key | null | undefined;
    nftAuctionBidderRef?: NftItemTile_nftAuctionBidderRef$key | null | undefined;
    viewer: NftItemTile_viewer$key;
    isMobile: boolean;
    showCreator?: boolean;
    fireTracking?: () => void;
    fireImpressionTracking?: () => void;
    gaLabel: string;
    currency: string;
}
interface RegularItemTileProps extends BaseProps {
    tileType: REGULAR_TILE;
    nftAuction: NftItemTile_nftAuction$key | null | undefined;
    showSpinner?: never;
}
interface CreatorRegularTileProps extends BaseProps {
    tileType: CREATOR_REGULAR;
    nftAuction: NftItemTile_nftAuction$key | null | undefined;
    showSpinner?: never;
}
interface CollectorTileProps extends BaseProps {
    tileType: COLLECTOR_TILE | typeof COLLECTOR_TILE_NO_CTA | COLLECTOR_TILE_NO_CREATOR;
    showSpinner?: boolean;
}

type Props = RegularItemTileProps | CreatorRegularTileProps | CollectorTileProps;

const nftItemFragment = graphql`
    fragment NftItemTile_nftItem on NftItem {
        wasBurned
        linkData {
            path
            isLinkable
        }
        ...NftItemTileMedia_item
        ...HeartPill_nftItem
        ...NftItemTileContent_nftItem
        ...ItemInternalContext_nftItem
    }
`;

const nftAuctionFragment = graphql`
    fragment NftItemTile_nftAuction on NftAuction {
        ...NftItemTileContent_nftAuction
        ...ItemInternalContext_nftAuction
    }
`;

const viewerFragment = graphql`
    fragment NftItemTile_viewer on Viewer {
        ...HeartPill_viewer
        ...NftItemTileContent_viewer
    }
`;

const auctionBidderRefFragment = graphql`
    fragment NftItemTile_nftAuctionBidderRef on NftAuctionBidderRef {
        ...ItemInternalContext_nftAuctionBidderRef
    }
`;

const isLoggedIn = (): boolean => {
    return typeof document !== 'undefined' && !!getBuyerId(document.cookie);
};

const NftItemTile: FC<Props> = props => {
    const {
        tileType,
        nftItem: nftItemRef,
        nftAuction: nftAuctionRef = null,
        nftAuctionBidderRef: nftAuctionBidderRef_ref = null,
        viewer: viewerRef,
        isMobile,
        fireTracking = () => {},
        fireImpressionTracking = () => {},
        gaLabel,
        showSpinner,
    } = props;

    const tileRef = useRef<HTMLDivElement | null>(null);
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const nftAuction = useFragment(nftAuctionFragment, nftAuctionRef);
    const nftAuctionBidderRef = useFragment(auctionBidderRefFragment, nftAuctionBidderRef_ref);
    const viewer = useFragment(viewerFragment, viewerRef);

    const { linkData, wasBurned } = nftItem;

    const [hovering, setHovering] = useState(false);
    const [visible, setVisible] = useState(false);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

    useEffect(() => {
        setIsUserLoggedIn(isLoggedIn());
    }, []);

    const heartContainerClass = classnames(styles.heartPill, {
        [styles.clickable]: isUserLoggedIn,
    });

    const disableLinks = !!wasBurned || tileType === COLLECTOR_TILE_NO_CTA;

    return (
        <div
            ref={tileRef}
            className={styles.container}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => setVisible(isVisible)}
                elementRef={tileRef}
                watchAfterFirstVisible
            />
            <VisibilityTracker
                onVisibilityChange={({ isVisible, disconnect }) => {
                    if (isVisible) {
                        disconnect();
                        fireImpressionTracking();
                    }
                }}
                elementRef={tileRef}
            />

            <MaybeLink
                linkData={disableLinks ? null : linkData}
                defaultTag={Fragment}
                buyerLinkType="standardLinkNoEffect"
                onClick={fireTracking}
            >
                <NftItemTileMedia
                    item={nftItem}
                    animate={visible}
                    isMobile={isMobile}
                    showIcon
                    showSpinner={showSpinner}
                />
            </MaybeLink>

            {disableLinks ? null : (
                <HeartPill
                    gaLabel={gaLabel}
                    viewer={viewer}
                    nftItem={nftItem}
                    visible={isMobile || hovering}
                    containerClass={heartContainerClass}
                />
            )}
            <ItemInternalContextProvider
                nftItem={nftItem}
                nftAuction={nftAuction}
                nftAuctionBidderRef={nftAuctionBidderRef}
            >
                <NftItemTileContent
                    {...props}
                    tileType={tileType}
                    nftItem={nftItem}
                    nftAuction={nftAuction}
                    viewer={viewer}
                />
            </ItemInternalContextProvider>
        </div>
    );
};

export { NftItemTile };
